// Shop Curation API client
//
// Follows the existing pattern in this codebase (see BookContext.js,
// BookDetailModal.js): no custom Express/Flask backend exists here --
// the "backend" is self-hosted Supabase, and components talk to it
// directly through the shared `supabase` client (PostgREST + Row Level
// Security enforce auth, not application code). This module wraps that
// pattern for the `curated_books` table so components don't duplicate
// query logic.
//
// Auth rule: user_id is NEVER taken from caller-supplied arguments here --
// it is always read from the current Supabase session
// (supabase.auth.getUser()), matching how RLS's `auth.uid() = user_id`
// policy (see migrations/40_curated_books.sql) independently enforces the
// same rule server-side even if a client were modified to try to spoof it.
import { supabase } from './supabase';

// GET /rest/v1/curated_books?book_id=eq.<book_id> (public read, no auth required)
export async function listCurations(bookId) {
  const { data, error } = await supabase
    .from('curated_books')
    .select('id, book_id, user_id, description, rating, timestamp')
    .eq('book_id', bookId)
    .order('timestamp', { ascending: false });
  if (error) throw error;
  return data || [];
}

// POST /rest/v1/curated_books (upsert) -- requires an authenticated session.
// Body shape: { book_id, description, rating }. user_id is derived from the
// session and rating is validated client-side (0-5) as a fast-fail UX
// affordance; the authoritative check is the DB CHECK constraint plus the
// curated_books_owner_write RLS policy, which cannot be bypassed by a
// tampered request.
export async function upsertCuration({ book_id, description, rating }) {
  const { data: { user }, error: authError } = await supabase.auth.getUser();
  if (authError || !user) {
    throw new Error('You must be signed in to curate a book.');
  }
  const numericRating = rating === null || rating === undefined || rating === ''
    ? null
    : Number(rating);
  if (numericRating !== null && (Number.isNaN(numericRating) || numericRating < 0 || numericRating > 5)) {
    throw new Error('Rating must be between 0 and 5.');
  }

  const { data, error } = await supabase
    .from('curated_books')
    .upsert(
      {
        book_id,
        user_id: user.id, // derived from session, never from caller input
        description: description ?? null,
        rating: numericRating,
      },
      { onConflict: 'book_id,user_id' }
    )
    .select()
    .single();
  if (error) throw error;
  return data;
}

// DELETE /rest/v1/curated_books?id=eq.<id> -- requires an authenticated
// session. RLS's curated_books_owner_write policy already restricts deletes
// to rows where auth.uid() = user_id, but we also scope the delete by
// user_id client-side so a mismatched id simply matches zero rows instead
// of silently relying on the database alone.
export async function deleteCuration(id) {
  const { data: { user }, error: authError } = await supabase.auth.getUser();
  if (authError || !user) {
    throw new Error('You must be signed in to remove a curation.');
  }
  const { error } = await supabase
    .from('curated_books')
    .delete()
    .eq('id', id)
    .eq('user_id', user.id);
  if (error) throw error;
}
